import React from 'react';
import type { ReactElement } from 'react';
import { FilterMd, spacingHelper } from '@construct-kit/core';
import styled, { css } from 'styled-components';
import { ResponsiveIconPillButton } from './filterStyles';
import { FilterPillChannel } from './FilterPillChannel';
import { FilterPillPropertyTypes } from './FilterPillPropertyTypes';
import { FilterPillPrice } from './FilterPillPrice';
import { FilterPillFloorArea } from './FilterPillFloorArea';
import { DEFAULT_FILTERS_REFINEMENTS_OPTIONS } from './mapRefinementsFilters';
import {
  smFloorAreaReqWidthPx,
  smPricePillReqWidthPx,
  smPropertyTypesPillReqWidthPx,
  lgPropertyTypesPillReqWidthPx,
  lgPricePillReqWidthPx,
  lgFloorAreaReqWidthPx,
} from '../layouts';
import { ResponsiveWrapper } from './ResponsiveWrapper';
import { getFilterCount } from './getFilterCount';
import type { SelectedFiltersOptionsType } from '../../../../shared/model/constants/filterTypes';
import type { ChannelObject } from '../../../../shared/model/tsTypes';
import { trackSearchRefinementModalOpen } from '../../../../shared/tracking/tealium/tracking';
import type { MapViewPropType } from '../../model/query/mapViewOptions';
import { Leased } from '../../../../shared/model/constants/channels';

export interface FiltersContainerProps {
  selectedFiltersOptions: SelectedFiltersOptionsType;
  initialFilterOptions: SelectedFiltersOptionsType;
  onFilterOpen: () => void;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  handleSearchOnClick: (onClose?: () => void) => void;
  handleChannelChange: (channel: ChannelObject) => void;
  mapView?: MapViewPropType;
}

const FilterPillChannelWrapper = styled.div`
  margin-right: ${spacingHelper('extraSmall')};
`;

const FilterPillPropertyTypesSmall = styled.div`
  display: none;
  margin-right: 0;

  @media (min-width: ${smPropertyTypesPillReqWidthPx}px) {
    display: flex;
    margin-right: ${spacingHelper('extraSmall')};
  }
`;

const FilterPillPropertyTypesLarge = styled.div`
  display: none;
  margin-right: 0;

  @media (min-width: ${lgPropertyTypesPillReqWidthPx}px) {
    display: flex;
    margin-right: ${spacingHelper('extraSmall')};
  }
`;

const FilterPillPriceSmall = styled.div`
  display: none;
  margin-right: 0;

  @media (min-width: ${smPricePillReqWidthPx}px) {
    display: flex;
    margin-right: ${spacingHelper('extraSmall')};
  }
`;

const FilterPillPriceLarge = styled.div`
  display: none;
  margin-right: 0;

  @media (min-width: ${lgPricePillReqWidthPx}px) {
    display: flex;
    margin-right: ${spacingHelper('extraSmall')};
  }
`;

const FilterPillFloorAreaSmall = styled.div`
  display: none;
  margin-right: 0;

  @media (min-width: ${smFloorAreaReqWidthPx}px) {
    display: flex;
    margin-right: ${spacingHelper('extraSmall')};
  }
`;

const FilterPillFloorAreaLarge = styled.div`
  display: none;
  margin-right: 0;

  @media (min-width: ${lgFloorAreaReqWidthPx}px) {
    display: flex;
    margin-right: ${spacingHelper('extraSmall')};
  }
`;

const AllFiltersBtnWrapper = styled.div`
  display: flex;
  align-self: flex-end;
  margin-right: ${spacingHelper('extraSmall')};
  ${({ theme }) => css`
    ${theme.layoutBreakpoints.extraLarge} {
      align-self: flex-start;
      margin: auto 0;
    }
  `};
`;

const FilterInline = styled.div`
  display: flex;
  flex-direction: row;
  > *:last-child {
    order: -1;
  }
  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    > *:last-child {
      order: 1;
    }
  }
`;

const trackPillClick = ({
  userAction,
  mapView,
}: {
  userAction: string;
  mapView?: MapViewPropType;
}) => {
  trackSearchRefinementModalOpen({
    searchMode: mapView ? 'map' : 'list',
    userAction,
  });
};

const FiltersContainer = ({
  selectedFiltersOptions,
  initialFilterOptions,
  setSelectedFiltersOptions,
  onFilterOpen,
  handleSearchOnClick,
  handleChannelChange,
  mapView,
}: FiltersContainerProps): ReactElement => {
  const { propertyTypes, priceRange, floorAreaRange } = selectedFiltersOptions;
  const {
    channel,
    propertyTypes: initialPropertyTypes,
    priceRange: initialPriceRange,
    floorAreaRange: initialFloorAreaRange,
  } = initialFilterOptions;
  const filterCount = getFilterCount(initialFilterOptions);
  const isLeased = channel.id === Leased.id;

  const onFilterPillClose = () => {
    setSelectedFiltersOptions(initialFilterOptions);
  };

  const handleFilterChange =
    <K extends keyof SelectedFiltersOptionsType>(filterName: K) =>
      (newValue: SelectedFiltersOptionsType[K]): void => {
        setSelectedFiltersOptions({
          ...selectedFiltersOptions,
          [filterName]: newValue,
        });
      };

  const handleFilterReset =
    <
      K extends keyof Omit<
        SelectedFiltersOptionsType,
        'channel' | 'includeSurroundingSuburbs' | 'searchLocations' | 'boundingBox'
      >,
    >(
      filterName: K,
    ) =>
      (): void => {
        setSelectedFiltersOptions({
          ...selectedFiltersOptions,
          [filterName]: DEFAULT_FILTERS_REFINEMENTS_OPTIONS[filterName],
        });
      };

  const trackPropertyTypesPillClick = () => {
    trackPillClick({ userAction: 'property-type-pill', mapView });
  };

  const trackPricePillClick = () => {
    trackPillClick({ userAction: 'price-pill', mapView });
  };

  const trackFloorAreaPillClick = () => {
    trackPillClick({ userAction: 'floor-area-pill', mapView });
  };

  const trackChannelPillClick = () => {
    trackPillClick({ userAction: 'property-channel-pill', mapView });
  };

  const filterPillPropertyTypes = (
    <FilterPillPropertyTypes
      selectedPropertyTypes={propertyTypes}
      initialPropertyTypes={initialPropertyTypes}
      onChange={handleFilterChange('propertyTypes')}
      onReset={handleFilterReset('propertyTypes')}
      onFilterPillClose={onFilterPillClose}
      handleSearchOnClick={handleSearchOnClick}
      trackPillClick={trackPropertyTypesPillClick}
    />
  );

  const filterPillPrice = (
    <FilterPillPrice
      selectedChannel={channel}
      selectedPriceRange={priceRange}
      initialPriceRange={initialPriceRange}
      onChange={handleFilterChange('priceRange')}
      onReset={handleFilterReset('priceRange')}
      onFilterPillClose={onFilterPillClose}
      handleSearchOnClick={handleSearchOnClick}
      trackPillClick={trackPricePillClick}
    />
  );

  const filterPillFloorArea = (
    <FilterPillFloorArea
      selectedFloorAreaRange={floorAreaRange}
      initialFloorAreaRange={initialFloorAreaRange}
      onChange={handleFilterChange('floorAreaRange')}
      onReset={handleFilterReset('floorAreaRange')}
      onFilterPillClose={onFilterPillClose}
      handleSearchOnClick={handleSearchOnClick}
      trackPillClick={trackFloorAreaPillClick}
    />
  );

  return (
    <FilterInline>
      <FilterPillChannelWrapper>
        <FilterPillChannel
          selectedChannel={channel}
          onChange={handleChannelChange}
          trackPillClick={trackChannelPillClick}
        />
      </FilterPillChannelWrapper>
      <ResponsiveWrapper
        smallScreenComponent={
          <FilterPillPropertyTypesSmall>{filterPillPropertyTypes}</FilterPillPropertyTypesSmall>
        }
        largeScreenComponent={
          <FilterPillPropertyTypesLarge>{filterPillPropertyTypes}</FilterPillPropertyTypesLarge>
        }
      />
      {!isLeased && (
        <ResponsiveWrapper
          smallScreenComponent={<FilterPillPriceSmall>{filterPillPrice}</FilterPillPriceSmall>}
          largeScreenComponent={<FilterPillPriceLarge>{filterPillPrice}</FilterPillPriceLarge>}
        />
      )}
      {!!mapView && (
        <ResponsiveWrapper
          smallScreenComponent={
            <FilterPillFloorAreaSmall>{filterPillFloorArea}</FilterPillFloorAreaSmall>
          }
          largeScreenComponent={
            <FilterPillFloorAreaLarge>{filterPillFloorArea}</FilterPillFloorAreaLarge>
          }
        />
      )}
      <AllFiltersBtnWrapper>
        <ResponsiveIconPillButton
          iconLeft={<FilterMd />}
          onClick={onFilterOpen}
          numberBadge={filterCount > 0 ? filterCount : undefined}
          selected={filterCount > 0}
          aria-label="All filters"
        >
          All filters
        </ResponsiveIconPillButton>
      </AllFiltersBtnWrapper>
    </FilterInline>
  );
};
export default FiltersContainer;
